import { useMutation } from "@tanstack/react-query";
import { useTheme } from "@/components/admin/use-theme";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  KeyRound,
  Languages,
  Sparkles,
  SunMoon,
} from "lucide-react";
import {
  Translate,
  useAuthProvider,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useLocaleState,
  useLocales,
  useLogout,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useRef, useState } from "react";

import { MobileContent } from "../layout/MobileContent";
import { MobileAdminGroup } from "../layout/MobileModulesSection";
import MobileHeader from "../layout/MobileHeader";
import { ChangelogPage } from "../misc/ChangelogPage";
import { UserAvatar } from "../misc/UserAvatar";
import type { CrmDataProvider } from "../providers/types";
import type { Sale } from "../types";
import { THEME_OPTIONS, themeOption } from "../misc/themeCycle";
import { McpUrlCopyRow } from "./McpUrlCopy";
import { MobileProfileSettings } from "./MobileProfileSettings";
import {
  SettingsGroup,
  SettingsRow,
  settingsRowClass,
} from "./MobileSettingsList";
import { formatFullName } from "../misc/personName";

type SettingsView = "root" | "profile";

/** Glyph size inside the rows' navy icon tile. */
const ROW_ICON = "size-[1.0625rem]";

/**
 * Account and settings as an iOS grouped list: a profile card that opens the
 * editable profile, then preferences, account, integrations, about and the
 * log out card. Used by the account sheet and the /settings mobile page.
 */
export const MobileSettingsBody = () => {
  const authProvider = useAuthProvider();
  const [view, setView] = useState<SettingsView>("root");
  const topRef = useRef<HTMLDivElement>(null);
  const shownView = useRef(view);

  // Each view starts at its top, like a pushed iOS screen. Only on an actual
  // view change: not on mount (also when StrictMode re-runs the effect), so
  // a remounted sheet keeps its restored scroll.
  useEffect(() => {
    if (shownView.current === view) return;
    shownView.current = view;
    topRef.current?.scrollIntoView?.({ block: "start" });
  }, [view]);

  if (!authProvider) return null;

  return (
    <div ref={topRef} className="scroll-mt-[var(--mobile-content-top)]">
      {view === "profile" ? (
        <ProfileView onBack={() => setView("root")} />
      ) : (
        <RootView onOpenProfile={() => setView("profile")} />
      )}
    </div>
  );
};

const RootView = ({ onOpenProfile }: { onOpenProfile: () => void }) => {
  const translate = useTranslate();
  const logout = useLogout();

  return (
    <div className="flex flex-col gap-7">
      <SettingsGroup>
        <ProfileSummaryRow onOpen={onOpenProfile} />
      </SettingsGroup>
      <SettingsGroup
        title={translate("crm.settings.preferences", { _: "Preferences" })}
      >
        <LanguageRow />
        <ThemeRow />
      </SettingsGroup>
      <SettingsGroup title={translate("crm.mobile.account_group")}>
        <ChangePasswordRow />
      </SettingsGroup>
      <MobileAdminGroup />
      <InboundEmailSection />
      <McpServerSection />
      <SettingsGroup title={translate("crm.settings.about")}>
        <SettingsRow
          icon={<Sparkles className={ROW_ICON} strokeWidth={2.1} />}
          label={translate("crm.changelog.title")}
          to={ChangelogPage.path}
          chevron
        />
      </SettingsGroup>
      <SettingsGroup>
        <SettingsRow
          label={<Translate i18nKey="ra.auth.logout">Log out</Translate>}
          onClick={() => logout()}
          destructive
        />
      </SettingsGroup>
    </div>
  );
};

const ProfileView = ({ onBack }: { onBack: () => void }) => {
  const translate = useTranslate();

  return (
    <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-right-6 duration-300 motion-reduce:animate-none">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          aria-label={translate("crm.mobile.settings_back")}
          className="liquid-glass glass-press flex size-11 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <ChevronLeft className="size-6" strokeWidth={2.25} />
        </button>
        <h3 className="text-[22px] font-bold tracking-tight">
          {translate("crm.profile.title", { _: "Profile" })}
        </h3>
      </div>
      <MobileProfileSettings />
    </div>
  );
};

const ProfileSummaryRow = ({ onOpen }: { onOpen: () => void }) => {
  const { identity } = useGetIdentity();
  const { data } = useGetOne<Sale>(
    "sales",
    { id: identity?.id },
    { enabled: identity?.id != null },
  );
  const fullName = formatFullName(data) || identity?.fullName || "";
  const avatar = data?.avatar?.src ?? identity?.avatar;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`${settingsRowClass} min-h-[76px] py-3`}
    >
      <UserAvatar src={avatar} name={fullName} className="size-14 text-xl" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[19px] font-semibold">{fullName}</span>
        {data?.email ? (
          <span className="truncate text-[15px] text-[color:var(--settings-secondary)]">
            {data.email}
          </span>
        ) : null}
      </span>
      <ChevronRight
        aria-hidden
        className="size-5 shrink-0 text-[color:var(--settings-chevron)]"
        strokeWidth={2.25}
      />
    </button>
  );
};

const ChangePasswordRow = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate: updatePassword } = useMutation({
    mutationKey: ["updatePassword"],
    mutationFn: async () => {
      if (!identity) {
        throw new Error(
          translate("crm.profile.record_not_found", {
            _: "Record not found",
          }),
        );
      }
      return dataProvider.updatePassword(identity.id);
    },
    onSuccess: () => {
      notify("crm.profile.password_reset_sent", {
        messageArgs: {
          _: "A reset password email has been sent to your email address",
        },
      });
    },
    onError: (e) => {
      notify(`${e}`, { type: "error" });
    },
  });

  return (
    <SettingsRow
      icon={<KeyRound className={ROW_ICON} strokeWidth={2.1} />}
      label={translate("crm.profile.password.change")}
      onClick={() => updatePassword()}
    />
  );
};

export const SettingsPageMobile = () => {
  const translate = useTranslate();

  return (
    <>
      <MobileHeader>
        <h1 className="text-xl font-semibold">
          {translate("crm.settings.title")}
        </h1>
      </MobileHeader>
      <MobileContent>
        <MobileSettingsBody />
      </MobileContent>
    </>
  );
};

SettingsPageMobile.path = "/settings";

const LanguageRow = () => {
  const translate = useTranslate();
  const locales = useLocales();
  const [locale, setLocale] = useLocaleState();

  if (locales.length <= 1) return null;

  const localeItems = locales.map(({ locale, name }) => ({
    value: locale,
    label: name,
  }));

  return (
    <SettingsRow
      icon={<Languages className={ROW_ICON} strokeWidth={2.1} />}
      label={translate("crm.language")}
      className="py-1.5"
      trailing={
        <Select
          value={locale}
          onValueChange={(value) => value && setLocale(value)}
          items={localeItems}
        >
          <SelectTrigger
            size="sm"
            aria-label={translate("crm.language")}
            className="!h-11 w-auto gap-1 border-none bg-transparent px-0 !text-[17px] text-[color:var(--settings-secondary)] shadow-none dark:bg-transparent"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {locales.map((language) => (
              <SelectItem key={language.locale} value={language.locale}>
                {language.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    />
  );
};

// The row shows the current theme; tapping it discloses the light / dark /
// system segmented control. Same setting, order, labels and icons as the
// desktop sidebar control (misc/themeCycle.ts).
const ThemeRow = () => {
  const translate = useTranslate();
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <SettingsRow
        icon={<SunMoon className={ROW_ICON} strokeWidth={2.1} />}
        label={translate("crm.theme.label", { _: "Theme" })}
        value={translate(themeOption(theme ?? "system").labelKey)}
        chevron
        expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen ? (
        <div className="px-4 py-3">
          <ToggleGroup
            value={theme ? [theme] : []}
            onValueChange={([value]) =>
              value && setTheme(value as "light" | "dark" | "system")
            }
            size="lg"
            variant="outline"
            spacing={0}
            className="w-full"
          >
            {THEME_OPTIONS.map(({ value, labelKey, icon: Icon }) => (
              <ToggleGroupItem
                key={value}
                value={value}
                aria-label={translate(labelKey)}
                className="flex-1 gap-2"
              >
                <Icon className="size-4" />
                {translate(labelKey)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      ) : null}
    </>
  );
};

const InboundEmailSection = () => {
  const translate = useTranslate();

  if (!import.meta.env.VITE_INBOUND_EMAIL) return null;

  return (
    <SettingsGroup
      title={translate("crm.profile.inbound.title")}
      footer={translate("crm.profile.inbound.description", {
        _: "You can start sending emails to your server's inbound email address, e.g. by adding it to the Cc: field. Avante will process the emails and add notes to the corresponding contacts.",
        field: "Cc:",
      })}
    >
      <CopyPasteRow value={import.meta.env.VITE_INBOUND_EMAIL} />
    </SettingsGroup>
  );
};

const McpServerSection = () => {
  const translate = useTranslate();

  return (
    <SettingsGroup
      title={translate("crm.profile.mcp.title", { _: "MCP Server" })}
      footer={translate("crm.profile.mcp.description", {
        _: "Use this URL to connect your AI assistant to your CRM data via the Model Context Protocol (MCP).",
      })}
    >
      <McpUrlCopyRow />
    </SettingsGroup>
  );
};

const CopyPasteRow = ({ value }: { value: string }) => {
  const translate = useTranslate();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    setCopied(true);
    navigator.clipboard.writeText(value);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              className={settingsRowClass}
              onClick={handleCopy}
            />
          }
        >
          <span className="min-w-0 flex-1 truncate">{value}</span>
          {copied ? (
            <Check className="size-5 shrink-0 text-[color:var(--settings-secondary)]" />
          ) : (
            <Copy className="size-5 shrink-0 text-[color:var(--settings-secondary)]" />
          )}
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {copied
              ? translate("crm.common.copied")
              : translate("crm.common.copy")}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
